import { useState, useEffect, useCallback } from 'react';

interface CacheMetadata {
    cacheId: string;
    cachedAt: string;
    expiresAt: string;
    status: string;
    hitCount: number;
    searchParams?: {
        departureId: string;
        arrivalId: string;
        checkIn: string;
        nights: number;
        adults: number;
        children: number;
    };
}

interface CachedOffer {
    offerId: string;
    roomType: string;
    roomCategory?: string;
    board: string;
    boardCode?: string;
    price: number;
    originalPrice?: number;
    currency: string;
    cancellationPolicy?: string;
    cancellationDate?: string;
    isRefundable?: boolean;
}

interface CachedHotelData {
    hotelId: string;
    productId?: string;
    name: string;
    stars: number;
    location: {
        city: string;
        country: string;
        countryCode?: string;
        region?: string;
        address?: string;
        latitude?: number;
        longitude?: number;
    };
    thumbnailUrl?: string;
    images: string[];
    description?: string;
    shortDescription?: string;
    facilities: string[];
    themes: string[];
    rating?: number;
    reviewCount?: number;
    instagramHandle?: string;
    lowestPrice?: number;
    lowestCurrency?: string;
    offers: CachedOffer[];
}

interface UseCachedHotelDataResult {
    cachedData: CachedHotelData | null;
    cacheMetadata: CacheMetadata | null;
    isCached: boolean;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

/**
 * Hook to fetch cached hotel data from the tour cache system.
 * Prioritizes cached data to reduce API calls and improve performance.
 * 
 * @param hotelId - The hotel ID to fetch cached data for
 * @returns Cached hotel data, metadata, and loading states
 */
export function useCachedHotelData(hotelId: string | undefined): UseCachedHotelDataResult {
    const [cachedData, setCachedData] = useState<CachedHotelData | null>(null);
    const [cacheMetadata, setCacheMetadata] = useState<CacheMetadata | null>(null);
    const [isCached, setIsCached] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchCachedData = useCallback(async () => {
        if (!hotelId) {
            setCachedData(null);
            setCacheMetadata(null);
            setIsCached(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/tour-cache/hotel/${hotelId}`);
            const data = await response.json();

            if (data.success && data.cached) {
                setCachedData(data.hotel);
                setCacheMetadata(data.cacheMetadata);
                setIsCached(true);
                console.log('[Cache] ✅ Loaded cached hotel data:', data.hotel.name);
            } else {
                setCachedData(null);
                setCacheMetadata(null);
                setIsCached(false);
                console.log('[Cache] ℹ️ No cached data available for hotel:', hotelId);
            }
        } catch (err) {
            console.error('[Cache] ❌ Error fetching cached data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch cached data');
            setCachedData(null);
            setCacheMetadata(null);
            setIsCached(false);
        } finally {
            setIsLoading(false);
        }
    }, [hotelId]);

    useEffect(() => {
        fetchCachedData();
    }, [fetchCachedData]);

    return {
        cachedData,
        cacheMetadata,
        isCached,
        isLoading,
        error,
        refetch: fetchCachedData
    };
}

/**
 * Verify price before purchase.
 * Calls the backend to check if the cached price matches current live price.
 * 
 * @param offerId - The offer ID to verify
 * @param cachedPrice - The cached price to compare
 * @param cachedCurrency - The cached currency
 * @returns Price verification result
 */
export async function verifyPriceBeforePurchase(
    offerId: string,
    cachedPrice: number,
    cachedCurrency: string = 'EUR'
): Promise<{
    success: boolean;
    verified: boolean;
    priceChanged: boolean | null;
    currentPrice?: number;
    currentCurrency?: string;
    priceDifference?: string;
    priceIncreased?: boolean;
    message: string;
}> {
    try {
        const response = await fetch('/api/tour-cache/verify-price', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ offerId, cachedPrice, cachedCurrency })
        });

        const data = await response.json();
        return data;
    } catch (err) {
        console.error('[PriceVerify] Error:', err);
        return {
            success: false,
            verified: false,
            priceChanged: null,
            message: 'Price verification failed'
        };
    }
}

export default useCachedHotelData;
